import React, { useState, useEffect } from 'react';
import { Login } from './components/Login';
import { Calendar } from './components/Calendar';
import { Stats } from './components/Stats';
import { ChoreList } from './components/ChoreList';
import { chores } from './data/chores';
import { assignChoresForWeek, getWeeklyStats } from './utils/choreAssignment';
import { AssignedChore, ViewMode, FilterMode } from './types';
import { Home, BarChart3, List, LogOut } from 'lucide-react';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignedChores, setAssignedChores] = useState<AssignedChore[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [activeTab, setActiveTab] = useState<'calendar' | 'stats' | 'chores'>('calendar');

  // Generate chores for the current week when date changes
  useEffect(() => {
    const newAssignedChores = assignChoresForWeek(currentDate);
    setAssignedChores(newAssignedChores);
  }, [currentDate]);

  const handleToggleChore = (choreId: string) => {
    setAssignedChores(prev => 
      prev.map(chore => 
        chore.id === choreId 
          ? { ...chore, completed: !chore.completed }
          : chore
      )
    );
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
  };

  const weeklyStats = getWeeklyStats(assignedChores, currentDate);

  if (!isLoggedIn) {
    return <Login onLogin={() => setIsLoggedIn(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Chore App</h1>
              <span className="text-sm text-gray-500">Mary & Maxwell</span>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'calendar'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Home className="h-4 w-4" />
              <span>Calendar</span>
            </button>
            
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'stats'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span>Stats</span>
            </button>
            
            <button
              onClick={() => setActiveTab('chores')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'chores'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="h-4 w-4" />
              <span>All Chores</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            <Stats stats={weeklyStats} />
            <Calendar
              currentDate={currentDate}
              assignedChores={assignedChores}
              viewMode={viewMode}
              filterMode={filterMode}
              onDateChange={setCurrentDate}
              onViewModeChange={setViewMode}
              onFilterChange={setFilterMode}
              onToggleChore={handleToggleChore}
            />
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Weekly Statistics</h2>
              <Stats stats={weeklyStats} />
            </div>
          </div>
        )}

        {activeTab === 'chores' && (
          <ChoreList chores={chores} />
        )}
      </main>
    </div>
  );
}

export default App; 